import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, Subscription } from 'rxjs';
import { filter, shareReplay, take } from 'rxjs/operators';

// Redux
import { dispatchAppError, dispatchInfoMessage, navigate, setBrowserTitle, setTitle } from 'src/app/actions/core.actions';
import * as BookmarkProblemListActions from 'src/app/actions/bookmark-problem-list.actions';
import * as BookmarkProblemsActions from 'src/app/actions/bookmark-problems.actions';
import {
  copyBookmarkProblemList,
  initializeCopyBookmarkProblemListState,
  initializeCreateBookmarkProblemListState
} from 'src/app/actions/bookmark-problem-list.actions';
import { RootState } from 'src/app/reducers';
import * as BookmarkProblemListSelectors from 'src/app/selectors/bookmark-problem-list.selectors';
import { getSubject } from 'src/app/selectors/static-data.selectors';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import { getCopyBookmarkProblemListResult } from 'src/app/selectors/bookmark-problem-list.selectors';

import { enter, fadeInOut } from 'src/app/resources/animations';
import {
  BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH,
  BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT,
  BOOKMARK_PROBLEMS_DEFAULT_MAX_COUNT,
  BOOKMARK_PROBLEMS_MAX_COUNT,
  DIALOG_ZERO_PADDING_PANEL_CLASS,
  OTHER_ORGANIZATION_ID
} from 'src/app/resources/config';
import {
  AddBookmarkProblemListDialogComponent,
  AddBookmarkProblemListDialogData
} from '../add-bookmark-problem-list-dialog/add-bookmark-problem-list-dialog.component';
import { BookmarkProblemListsUtil } from 'src/app/utils/bookmark-problem-lists-util';

import { User } from 'src/app/models/user';
import { MenuTab } from 'src/app/models/menu';
import { Subject } from '../../../models/common-data';

import { GeneralError } from '../../../errors/general-error';
import { Dates } from 'src/app/utils/dates';
import {
  ChangeBookmarkProblemListDialogComponent,
  ChangeBookmarkProblemListDialogData
} from '../change-bookmark-problem-list-dialog/change-bookmark-problem-list-dialog.component';
import {
  DeleteBookmarkProblemListDialogComponent,
  DeleteBookmarkProblemListDialogData
} from '../delete-bookmark-problem-list-dialog/delete-bookmark-problem-list-dialog.component';
import { Log } from 'src/app/utils/log';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import { Visibility, VisibilityType } from '../../../models/create-bookmark-problem-list';
import { TabVisibilityService } from 'src/app/services/tab-visibility.service';
import { FindMyBookmarkProblemListParams, FindSharedBookmarkProblemListParams } from 'src/app/models/bookmark-problem-list';
import { FindBookmarkProblemsParams } from 'src/app/models/bookmark-problem';
import { BookmarkProblemListRouterService } from 'src/app/services/bookmark-problem-list-router.service';

interface ProblemListData {
  id: string;
  listName: string;
  problemCount: number;
  owner: string;
  visibility?: string;
  updatedAt: string;
}

@Component({
  selector: 'app-bookmark-problem-lists',
  templateUrl: './bookmark-problem-lists.component.html',
  styleUrls: ['./bookmark-problem-lists.component.scss'],
  animations: [enter, fadeInOut]
})
export class BookmarkProblemListsComponent implements OnInit, OnDestroy {
  @ViewChild('pageTop') pageTop: ElementRef;
  subjects$: Observable<Subject[]>;
  subjectId$: Observable<string>;
  signedInUser$: Observable<User>;
  menus: MenuTab[] = [
    {
      key: 'own',
      label: '自分のお気に入りリスト',
      isActive: true
    }
  ];
  isOwnTabSelected = true;
  isListsLoading = true;
  isLimitExceeded = false;
  selectedSubjectId: string;
  listMaxCount = BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT;
  problemMaxCount = BOOKMARK_PROBLEMS_DEFAULT_MAX_COUNT;

  protected readonly VisibilityType = VisibilityType;

  private LOG_SOURCE = this.constructor.name;
  private title = 'お気に入り管理';

  dataSource = new MatTableDataSource<ProblemListData>([]);
  columns: string[] = ['listName', 'problemCount', 'owner', 'visibility', 'updatedAt', 'action'];

  private visibilitySub!: Subscription;

  constructor(
    private store: Store<RootState>,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private tabVisibility: TabVisibilityService,
    private bookmarkProblemListRouterService: BookmarkProblemListRouterService
  ) {}

  ngOnInit() {
    // お気に入り管理のタブが再度開かれた場合は、データを取得し直してテーブルにデータを設定(別タブで更新があった場合に反映するため)
    this.visibilitySub = this.tabVisibility.onVisibilityChange().subscribe(state => {
      if (state === 'visible') {
        Log.debug(this.LOG_SOURCE, 'お気に入り管理のタブが再度開かれました ', state);
        this.setUpBookmarkProblemLists();
        this.setUpBookmarkProblems();
        if (this.isOwnTabSelected) {
          this.setUpMyLists();
        } else {
          this.setUpSharedLists();
        }
      }
      return;
    });

    this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));
    this.setUpUser();
    this.setUpQueryParams();
    this.setUpSubjects();
    this.setUpListMaxCount();
    this.setUpTabMenus();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeCreateBookmarkProblemListState());
    this.store.dispatch(initializeCopyBookmarkProblemListState());
    this.visibilitySub.unsubscribe();
  }

  alert(message: string) {
    window.alert(message);
  }

  onChangeSubject() {
    this.isListsLoading = true;
    if (this.isOwnTabSelected) {
      this.setUpMyLists();
    } else {
      this.setUpSharedLists();
    }

    this.store.dispatch(
      navigate({ url: RoutingPathResolver.resolveBookmarkProblemLists(), extras: { queryParams: { subjectId: this.selectedSubjectId } } })
    );
  }

  menuClickHandler(key) {
    switch (key) {
      case 'own':
        this.isOwnTabSelected = true;
        this.isListsLoading = true;

        this.setUpMyLists();
        this.columns = ['listName', 'problemCount', 'owner', 'visibility', 'updatedAt', 'action'];

        // メニュータブのオンオフを切り替え
        this.menus[0].isActive = true;
        this.menus[1].isActive = false;
        break;

      case 'shared':
        this.isOwnTabSelected = false;
        this.isListsLoading = true;
        this.setUpSharedLists();

        this.columns = ['listName', 'problemCount', 'owner', 'updatedAt', 'action'];
        // メニュータブのオンオフを切り替え
        this.menus[0].isActive = false;
        this.menus[1].isActive = true;
        break;
    }
  }

  addBookmarkProblemList() {
    const data: AddBookmarkProblemListDialogData = { subjectId: this.selectedSubjectId };
    const config: MatDialogConfig = {
      width: BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data,
      disableClose: true
    };
    this.dialog
      .open(AddBookmarkProblemListDialogComponent, config)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          if (this.isOwnTabSelected) {
            this.setUpMyLists();
          } else {
            this.setUpSharedLists();
          }
        }
      });
  }

  updateBookmarkProblemList(id: string, listName: string, visibility: Visibility) {
    const data: ChangeBookmarkProblemListDialogData = { id, listName, visibility };
    const config: MatDialogConfig = {
      width: BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data,
      disableClose: true
    };
    this.dialog
      .open(ChangeBookmarkProblemListDialogComponent, config)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          if (this.isOwnTabSelected) {
            this.setUpMyLists();
          } else {
            this.setUpSharedLists();
          }
        }
      });
  }

  copyBookmarkProblemList(bookmarkProblemListId: string, listName: string) {
    Log.debug(this.LOG_SOURCE, 'お気に入りリストを複製します');

    this.store.dispatch(
      dispatchInfoMessage({
        message: `${listName}を複製しています。`
      })
    );

    const request = { bookmarkProblemListId };
    this.store.dispatch(copyBookmarkProblemList(request));

    this.store
      .select(getCopyBookmarkProblemListResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        if (result.success) {
          this.isOwnTabSelected ? this.setUpMyLists() : this.setUpIsLimitExceed();

          const message = `「${result.response.name}」を自分のお気に入りリスト内に作成しました`;
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          this.store.dispatch(initializeCopyBookmarkProblemListState());

          // Firestoreよりお気に入りリストに紐づく情報を再取得（リストはreducerでコピーされる）
          this.setUpBookmarkProblems();
          return;
        }

        Log.warn(this.LOG_SOURCE, `お気に入りリストの複製エラー: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        const message = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
        this.store.dispatch(
          dispatchInfoMessage({
            message
          })
        );
        this.store.dispatch(initializeCopyBookmarkProblemListState());
      });
  }

  deleteBookmarkProblemList(id: string, listName: string) {
    const data: DeleteBookmarkProblemListDialogData = { id, listName };
    const config: MatDialogConfig = {
      width: BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH, // Todo: ADDを削除
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data,
      disableClose: true
    };
    this.dialog
      .open(DeleteBookmarkProblemListDialogComponent, config)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          // Firestoreより問題情報を再取得 - 削除されたリストの問題は不要のため
          this.setUpBookmarkProblems();

          if (this.isOwnTabSelected) {
            this.setUpMyLists();
          } else {
            this.setUpSharedLists();
          }
        }
      });
  }

  goToProblemListPage(visibility: string, listId: string) {
    if (this.isOwnTabSelected && visibility === VisibilityType.UNCATEGORIZED) {
      this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemListsUnlisted(this.selectedSubjectId) }));
    } else {
      this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemListsDetail(listId) }));
    }
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null && it !== 'none'));
  }

  private setUpQueryParams() {
    this.subjectId$ = this.activatedRoute.queryParamMap.pipe(
      shareReplay(),
      map(queryParamMap => queryParamMap.get('subjectId') || '')
    );
  }

  private setUpSubjects() {
    this.subjects$ = this.store.select(getSubject).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    combineLatest([this.subjectId$, this.subjects$, this.signedInUser$])
      .pipe(take(1))
      .subscribe(([subjectId, subjects, signedInUser]) => {
        signedInUser.organization;
        if (subjects.length > 0) {
          const _subjects = [];
          subjects.forEach(subject => {
            if (signedInUser.visibleSubjectIds.includes(subject.id)) {
              _subjects.push(subject);
            }
          });

          subjects = _subjects;
          this.subjects$ = of(subjects);
        }

        if (subjects.length === 0) {
          this.store.dispatch(
            dispatchAppError({
              source: this.LOG_SOURCE,
              error: GeneralError.unknown(`subject が 0 件です`)
            })
          );
          return;
        }

        if (subjectId && !signedInUser.visibleSubjectIds.includes(subjectId)) {
          this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemLists() }));
          return;
        }

        this.selectedSubjectId = subjectId && signedInUser.visibleSubjectIds.includes(subjectId) ? subjectId : subjects[0].id;
      });
  }

  private setUpTabMenus() {
    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        if (signedInUser.organization !== OTHER_ORGANIZATION_ID) {
          // 団体種別がその他の場合は共有されたリストタブを表示しない
          this.menus.push({
            key: 'shared',
            label: 'メンバーから共有されたリスト',
            isActive: false
          });

          // 共有されたリストの問題一覧ページから戻ってきた場合は、共有されたリストのタブを開く
          const state = this.bookmarkProblemListRouterService.getState();
          if (state === 'sharedList') {
            this.menus[0].isActive = false;
            this.menus[1].isActive = true;
            this.bookmarkProblemListRouterService.clearState();
            this.isOwnTabSelected = false;
            this.setUpSharedLists();
            this.columns = ['listName', 'problemCount', 'owner', 'updatedAt', 'action'];
          } else {
            this.setUpMyLists();
          }
        }
      });
  }

  private setUpListMaxCount() {
    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        this.listMaxCount = BookmarkProblemListsUtil.getListsMaxCount(signedInUser.schoolId);
        this.problemMaxCount = BookmarkProblemListsUtil.getProblemsMaxCount(signedInUser.schoolId);
      });
  }

  private setUpMyLists() {
    this.store
      .select(BookmarkProblemListSelectors.getBookmarkProblemLists)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(bookmarkProblemLists => {
        const filteredBookmarkProblemListsBySubjectId = bookmarkProblemLists.filter(
          bookmarkProblemList => bookmarkProblemList.subjectId === this.selectedSubjectId
        );
        this.isLimitExceeded =
          filteredBookmarkProblemListsBySubjectId.filter(bookmarkProblemList => !bookmarkProblemList.id.includes('uncategorized')).length >=
          this.listMaxCount
            ? true
            : false;
        const lists: ProblemListData[] = filteredBookmarkProblemListsBySubjectId.map(list => {
          return {
            id: list.id,
            listName: list.name,
            owner: '自分',
            visibility: list.visibility,
            visibilityLabel:
              list.visibility === VisibilityType.PRIVATE
                ? '非公開'
                : list.visibility === VisibilityType.SCHOOL_ONLY
                ? '団体全員に共有中'
                : '共有できません',
            problemCount: list.problemCount,
            updatedAt:
              list.visibility === VisibilityType.PRIVATE || list.visibility === VisibilityType.SCHOOL_ONLY
                ? Dates.stdDateStringFromIso(list.updatedAt)
                : '-'
          };
        });

        // 未分類のリストのを先頭に、お気に入りリスト名の名前の昇順にならびかえ
        const collator = new Intl.Collator('ja', { sensitivity: 'base' });
        lists.sort((a, b) => {
          if (a.id === `uncategorized_${this.selectedSubjectId}`) return -1;
          if (b.id === `uncategorized_${this.selectedSubjectId}`) return 1;
          return collator.compare(a.listName, b.listName);
        });

        this.dataSource = new MatTableDataSource(lists);
        this.isListsLoading = false;
      });
  }

  private setUpSharedLists() {
    this.store
      .select(BookmarkProblemListSelectors.getSharedBookmarkProblemLists)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(bookmarkProblemLists => {
        const lists: ProblemListData[] = bookmarkProblemLists
          .filter(bookmarkProblemList => bookmarkProblemList.subjectId === this.selectedSubjectId)
          .map(list => {
            return {
              id: list.id,
              listName: list.name,
              owner: list.ownerName,
              problemCount: list.problemCount,
              updatedAt:
                list.visibility === VisibilityType.PRIVATE || list.visibility === VisibilityType.SCHOOL_ONLY
                  ? Dates.stdDateStringFromIso(list.updatedAt)
                  : '-'
            };
          });

        // 未分類のリストのを先頭に、お気に入りリスト名の名前の昇順にならびかえ
        const collator = new Intl.Collator('ja', { sensitivity: 'base' });
        lists.sort((a, b) => {
          return collator.compare(a.listName, b.listName);
        });

        this.dataSource = new MatTableDataSource(lists);
        this.isListsLoading = false;
      });
  }

  private setUpBookmarkProblemLists() {
    this.store.dispatch(BookmarkProblemListActions.initializeFindMyBookmarkProblemListsState());
    this.store.dispatch(BookmarkProblemListActions.initializeFindSharedBookmarkProblemListsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findMyBookmarkProblemListParams: FindMyBookmarkProblemListParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemListActions.findMyBookmarkProblemLists(findMyBookmarkProblemListParams));

        const findSharedBookmarkProblemListParams: FindSharedBookmarkProblemListParams = {
          schoolId: signedInUser.schoolId,
          userId: signedInUser.id
        };
        this.store.dispatch(BookmarkProblemListActions.findSharedBookmarkProblemLists(findSharedBookmarkProblemListParams));
      });
  }

  private setUpBookmarkProblems() {
    this.store.dispatch(BookmarkProblemsActions.initializeFindBookmarkProblemsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findBookmarkProblemsParams: FindBookmarkProblemsParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemsActions.findBookmarkProblems(findBookmarkProblemsParams));
      });
  }

  private setUpIsLimitExceed() {
    // リストの上限をチェックする
    this.store
      .select(BookmarkProblemListSelectors.getBookmarkProblemLists)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(bookmarkProblemLists => {
        const filteredBookmarkProblemListsBySubjectId = bookmarkProblemLists.filter(
          bookmarkProblemList => bookmarkProblemList.subjectId === this.selectedSubjectId
        );
        this.isLimitExceeded =
          filteredBookmarkProblemListsBySubjectId.filter(bookmarkProblemList => !bookmarkProblemList.id.includes('uncategorized')).length >=
          this.listMaxCount
            ? true
            : false;
      });
  }
}
